import { useTranslation } from "react-i18next";

export type StatusFilter = "all" | "completed" | "inProgress" | "notStarted";

interface StatusCounts {
  all: number;
  completed: number;
  inProgress: number;
  notStarted: number;
}

interface EnduserStatusTabsProps {
  activeStatus: StatusFilter;
  onStatusChange: (status: StatusFilter) => void;
  counts: StatusCounts;
}

const EnduserStatusTabs = ({ activeStatus, onStatusChange, counts }: EnduserStatusTabsProps) => {
  const { t } = useTranslation();

  const tabs: { key: StatusFilter; label: string; color: string; bgColor: string }[] = [
    {
      key: "all",
      label: t("enduserStats.status.all"),
      color: "text-gray-700",
      bgColor: "bg-gray-100",
    },
    {
      key: "notStarted",
      label: t("enduserStats.status.notStarted"),
      color: "text-blue-700",
      bgColor: "bg-blue-100",
    },
    {
      key: "inProgress",
      label: t("enduserStats.status.inProgress"),
      color: "text-yellow-700",
      bgColor: "bg-yellow-100",
    },
    {
      key: "completed",
      label: t("enduserStats.status.completed"),
      color: "text-green-700",
      bgColor: "bg-green-100",
    },
  ];

  return (
    <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
      {tabs.map((tab) => {
        const isActive = activeStatus === tab.key;
        const count = counts[tab.key];

        return (
          <button
            key={tab.key}
            onClick={() => onStatusChange(tab.key)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              isActive
                ? `${tab.bgColor} ${tab.color}`
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            {/* Status icon */}
            {tab.key === "completed" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 ${isActive ? "text-green-600" : "text-gray-400"}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
            {tab.key === "inProgress" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 ${isActive ? "text-yellow-600" : "text-gray-400"}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            )}
            {tab.key === "notStarted" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 ${isActive ? "text-blue-600" : "text-gray-400"}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            )}
            {tab.key === "all" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 ${isActive ? "text-gray-600" : "text-gray-400"}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            )}

            <span>{tab.label}</span>
            <span
              className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-medium ${
                isActive ? tab.bgColor : "bg-gray-100"
              } ${isActive ? tab.color : "text-gray-500"}`}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default EnduserStatusTabs;
